import React, { useEffect, useState } from "react";
import { getAdminDrawingsWithNodes, deleteDrawingSheet } from "../../services/operations/drawingsheetAPI";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { FileText, Users, Eye, Trash2, Loader2, Image as ImageIcon, ChevronRight } from "lucide-react";

const ViewAllDrawingsPage = () => {
  const [drawings, setDrawings] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this Drawing Sheet?")) {
      const result = await deleteDrawingSheet(id);
      if (result?.success) {
        setDrawings((prev) => prev.filter((d) => d._id !== id));
      } else {
        alert("Error deleting the drawing.");
      }
    }
  };

  useEffect(() => {
    const fetchDrawings = async () => {
      const response = await getAdminDrawingsWithNodes();
      if (response) {
        setDrawings(response);
      }
      setLoading(false);
    };
    fetchDrawings();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Modern Header */}
        <div className=" mb-12">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-xl">
              <FileText className="h-6 w-6 text-white" />
            </div>
          
          <h1 className="text-3xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
            All Drawings Created
          </h1>
          </div>
          <p className="text-slate-600 text-lg ml-12">Manage and view all your HAZOP drawing sheets</p>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <Loader2 className="animate-spin h-12 w-12 text-blue-500 mx-auto mb-4" />
              <p className="text-slate-600">Loading drawings...</p>
            </div>
          </div>
        ) : drawings.length === 0 ? (
          <div className="text-center py-20">
            <div className="p-6 bg-slate-100 rounded-2xl w-fit mx-auto mb-6">
              <FileText className="h-16 w-16 text-slate-400 mx-auto" />
            </div>
            <p className="text-slate-500 text-xl">No drawings created yet.</p>
            <p className="text-slate-400 mt-2">Create your first drawing to get started</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {drawings.map((drawing) => (
              <div
                key={drawing._id}
                className="group relative overflow-hidden bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 transition-all duration-500 hover:shadow-2xl hover:scale-[1.02] hover:-translate-y-2"
              >
                {/* Delete Button */}
                <button
                  onClick={() => handleDelete(drawing._id)}
                  className="absolute top-4 right-4 z-10 p-2 bg-red-500/90 backdrop-blur-sm text-white rounded-full opacity-0 group-hover:opacity-100 hover:bg-red-600 transition-all duration-300 shadow-lg hover:scale-110"
                  title="Delete Drawing Sheet"
                >
                  <Trash2 className="h-4 w-4" />
                </button>

                {/* Drawing Image */}
                <div className="relative h-48 bg-gradient-to-br from-slate-100 to-gray-200 overflow-hidden">
                  {drawing?.drawingImage ? (
                    <img 
                      src={drawing.drawingImage} 
                      alt={`Drawing ${drawing.drawingNumber}`}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageIcon className="h-16 w-16 text-slate-400" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <div className="mb-4">
                    <h2 className="text-xl font-bold text-slate-800 mb-2">
                      Drawing No: 
                      <span className="text-blue-600 ml-2 px-3 py-1 bg-blue-100 rounded-lg text-sm">
                        {drawing.drawingNumber}
                      </span>
                    </h2>
                  </div>

                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-gradient-to-br from-emerald-500 to-green-600 rounded-lg">
                        <Users className="h-4 w-4 text-white" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Chairman</p>
                        <p className="text-slate-800 font-semibold">
                          {drawing?.chairman?.firstName} {drawing?.chairman?.lastName}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg">
                        <Users className="h-4 w-4 text-white" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Scribe</p>
                        <p className="text-slate-800 font-semibold">
                          {drawing?.scribe?.firstName} {drawing?.scribe?.lastName}
                        </p>
                      </div>
                    </div>

                    <div className="pt-2">
                      <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Team Members</p>
                      <div className="flex flex-wrap gap-2">
                        {drawing.team.map((member, idx) => (
                          <span
                            key={idx}
                            className="px-3 py-1 bg-gradient-to-r from-slate-100 to-gray-100 text-slate-700 text-sm rounded-full font-medium shadow-sm"
                          >
                            {member.firstName} {member.lastName}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Action Section */}
                  {drawing.nodes.length > 0 ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-emerald-600">
                        <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                        <span className="text-sm font-medium">{drawing.nodes.length} nodes created</span>
                      </div>
                      <button
                        className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white py-3 px-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 transform hover:scale-105 font-semibold"
                        onClick={() =>
                          navigate(`/dashboard/employee/view-nodes/${drawing._id}`)
                        }
                      >
                        <Eye className="h-5 w-5" />
                        View Nodes
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <div className="p-3 bg-amber-100 rounded-xl mb-3">
                        <FileText className="h-6 w-6 text-amber-600 mx-auto" />
                      </div>
                      <p className="text-slate-500 italic text-sm">
                        No nodes created under this drawing yet.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewAllDrawingsPage;